import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import MatrixBackground from '../../components/ui/MatrixBackground';

interface CheckItem {
  id: string;
  text: string;
}

interface Section {
  id: string;
  icon: string;
  title: string;
  subtitle: string;
  color: string;
  items: CheckItem[];
  details: React.ReactNode;
}

function ChecklistItem({ item, checked, onToggle }: { item: CheckItem; checked: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      className="flex items-start gap-3 w-full text-left group py-1.5"
    >
      <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${checked ? 'bg-green-500 border-green-500' : 'border-gray-600 group-hover:border-green-500/50'}`}>
        {checked && (
          <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
      <span className={`text-sm leading-relaxed transition-colors ${checked ? 'text-gray-500 line-through' : 'text-gray-300 group-hover:text-white'}`}>
        {item.text}
      </span>
    </button>
  );
}

function SectionCard({ section, index }: { section: Section; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const toggleCheck = (id: string) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const completedCount = checked.size;
  const totalCount = section.items.length;
  const pct = Math.round((completedCount / totalCount) * 100);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="rounded-xl border border-green-500/10 bg-gray-900/70 overflow-hidden"
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-4 p-5 md:p-6 text-left hover:bg-gray-800/30 transition-colors"
      >
        <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gray-800 border border-green-500/20 flex items-center justify-center text-2xl">
          {section.icon}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-white">{section.title}</h3>
          <p className="text-sm text-gray-400 mt-0.5">{section.subtitle}</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="hidden sm:flex items-center gap-2">
            <div className="w-20 h-2 rounded-full bg-gray-800 overflow-hidden">
              <div
                className="h-full rounded-full bg-green-500 transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-xs text-gray-500 w-8">{pct}%</span>
          </div>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      <div className={`overflow-hidden transition-all duration-500 ease-in-out ${expanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-5 md:px-6 pb-6 border-t border-green-500/10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-5">
            <div>
              <h4 className="text-sm font-semibold text-green-400 uppercase tracking-wider mb-3">Checklist</h4>
              <div className="space-y-1">
                {section.items.map(item => (
                  <ChecklistItem
                    key={item.id}
                    item={item}
                    checked={checked.has(item.id)}
                    onToggle={() => toggleCheck(item.id)}
                  />
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-green-400 uppercase tracking-wider mb-3">Detalle</h4>
              <div className="text-sm text-gray-400 leading-relaxed space-y-3">
                {section.details}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

const sections: Section[] = [
  {
    id: 'github',
    icon: '📁',
    title: 'Repositorio en GitHub',
    subtitle: 'Tu proyecto como portfolio permanente con experiencia comprobable',
    color: 'green',
    items: [
      { id: 'gh-1', text: 'Subir el proyecto a GitHub (repositorio público)' },
      { id: 'gh-2', text: 'README con Markdown: overview claro y conciso del proyecto' },
      { id: 'gh-3', text: 'Incluir capturas del dashboard y visualizaciones' },
      { id: 'gh-4', text: 'Mostrar la arquitectura usada (Bronze/Silver/Gold, APIs, modelado)' },
      { id: 'gh-5', text: 'Preguntas de negocio: qué problema resolvías, qué resolviste, qué insights encontraste' },
      { id: 'gh-6', text: 'Overview personal breve: quién sos, qué te motivó, links a tus perfiles' },
    ],
    details: (
      <>
        <p>
          El repo en GitHub es tu carta de presentación técnica. Es lo primero que mira un recruiter
          o un lead técnico cuando quiere ver si sabés lo que decís que sabés.
        </p>
        <p>
          <strong className="text-white">El README es clave.</strong> No escribas 3 páginas. Hacelo conciso:
          qué hace el proyecto, qué tecnologías usa, una captura del dashboard o resultado,
          y la arquitectura de datos. Lo visual es lo más importante — es lo primero que impacta.
        </p>
        <p>
          <strong className="text-white">Sé nítido con las preguntas de negocio.</strong> No alcanza con
          mostrar código. Mostrá qué problema buscabas resolver, cómo lo resolviste y qué descubriste.
          Eso demuestra pensamiento analítico, no solo habilidad técnica.
        </p>
        <div className="mt-4 p-3 rounded-lg bg-green-500/5 border border-green-500/10">
          <p className="text-green-400 text-xs font-semibold uppercase tracking-wider mb-1">Ejemplo de estructura README</p>
          <div className="text-gray-400 text-xs font-mono space-y-0.5">
            <p># Nombre del Proyecto</p>
            <p>## Problema / Contexto</p>
            <p>## Arquitectura</p>
            <p>{'## Screenshot del Dashboard'}</p>
            <p>## Tecnologías usadas</p>
            <p>## Insights principales</p>
            <p>## Sobre mí</p>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'video',
    icon: '🎥',
    title: 'Video de presentación',
    subtitle: 'Lo que más te va a diferenciar — la mayoría en tech NO hace esto',
    color: 'blue',
    items: [
      { id: 'vid-1', text: 'Grabar un video de 5 a 10 minutos (no más)' },
      { id: 'vid-2', text: 'Con cara visible, o al menos con tu voz' },
      { id: 'vid-3', text: 'Explicar qué problema o necesidad tenías' },
      { id: 'vid-4', text: 'Mostrar qué arquitectura y modelado usaste' },
      { id: 'vid-5', text: 'Mostrar cómo procesaste los datos (ingesta, transformaciones, capas)' },
      { id: 'vid-6', text: 'Enseñar los insights del dashboard' },
      { id: 'vid-7', text: 'Cerrar con: qué resolviste y cuáles son los resultados' },
    ],
    details: (
      <>
        <p>
          Un video de presentación es una <strong className="text-white">carta de presentación tremenda</strong>.
          Muestra tres cosas que un recruiter valora mucho: iniciativa, creatividad y habilidad de comunicación.
        </p>
        <p>
          <strong className="text-white">Duración: 5 a 10 minutos.</strong> Nadie mira un video de 45 minutos.
          En las principales revistas académicas ahora están pidiendo videos de 5-7 minutos explicando los papers.
          La gente técnica tiene cada vez menos tiempo. Un video corto y bien hecho lo mira cualquiera.
        </p>
        <p>
          <strong className="text-white">Mostrá tu cara.</strong> Genera conexión. Si no podés con cámara,
          al menos grabá con tu voz explicando sobre la pantalla del proyecto. Que se escuche quién está
          detrás del código.
        </p>
        <div className="mt-4 p-3 rounded-lg bg-green-500/5 border border-green-500/10">
          <p className="text-green-400 text-xs font-semibold uppercase tracking-wider mb-1">Estructura sugerida</p>
          <ol className="text-gray-400 text-xs space-y-1 list-decimal list-inside">
            <li>Qué problema o necesidad tenías (1-2 min)</li>
            <li>Qué arquitectura y modelado usaste (2 min)</li>
            <li>Cómo procesaste los datos — ingesta, transformaciones, capas (2-3 min)</li>
            <li>Insights del dashboard — qué descubriste (2 min)</li>
            <li>Cierre: qué resolviste y resultados (1 min)</li>
          </ol>
        </div>
      </>
    ),
  },
  {
    id: 'linkedin',
    icon: '💼',
    title: 'LinkedIn y difusión',
    subtitle: 'Que te vean, te conozcan y empiecen a llegar oportunidades',
    color: 'sky',
    items: [
      { id: 'li-1', text: 'Publicar el proyecto en LinkedIn con un post dedicado' },
      { id: 'li-2', text: 'Incluir link al repo de GitHub en el post' },
      { id: 'li-3', text: 'Incluir link al video de presentación' },
      { id: 'li-4', text: 'Agregar el proyecto a la sección "Proyectos" del perfil de LinkedIn' },
      { id: 'li-5', text: 'Describir en el post qué resolviste y qué aprendiste (no solo "terminé el bootcamp")' },
    ],
    details: (
      <>
        <p>
          LinkedIn es donde están los recruiters. Si el proyecto está en GitHub y hay un video explicándolo,
          LinkedIn es el canal para que todo eso llegue a las personas correctas.
        </p>
        <p>
          <strong className="text-white">No alcanza con decir "terminé el bootcamp".</strong> Contá qué
          problema resolviste, qué tecnologías usaste, qué descubriste. El post tiene que generar curiosidad
          para que hagan clic en tu repo y tu video.
        </p>
        <p>
          <strong className="text-white">Links cruzados:</strong> Tu post de LinkedIn lleva al repo.
          Tu repo tiene el link del video. Tu video menciona tu LinkedIn. Todo conectado.
          Un recruiter que entra por cualquier punto puede ver todo tu trabajo.
        </p>
        <div className="mt-4 p-3 rounded-lg bg-green-500/5 border border-green-500/10">
          <p className="text-green-400 text-xs font-semibold uppercase tracking-wider mb-1">Tip para el post</p>
          <p className="text-gray-400 text-xs">
            Arrancá con el problema, no con la tecnología. "Quería predecir X" engancha más que
            "Usé Databricks con Spark". El problema genera curiosidad, la tecnología la podés contar después.
          </p>
        </div>
      </>
    ),
  },
  {
    id: 'diferencial',
    icon: '⭐',
    title: 'Tu diferencial personal',
    subtitle: 'Lo que te hace único no es solo saber Data Engineering — es tu historia',
    color: 'amber',
    items: [
      { id: 'dif-1', text: 'Identificar qué de tu historia personal te diferencia' },
      { id: 'dif-2', text: 'Elegir un proyecto que conecte con algo que te apasiona' },
      { id: 'dif-3', text: 'Comunicar tus habilidades blandas a través del proyecto (liderazgo, análisis, creatividad)' },
      { id: 'dif-4', text: 'Si tuviste entrevistas técnicas no exitosas, convertir esos challenges en proyectos de portfolio' },
    ],
    details: (
      <>
        <p>
          <strong className="text-white">Tu historia personal es valor agregado puro.</strong> Lo que te hace
          único no es solo saber Data Engineering — es tu background. Si fundaste una escuela de artes marciales,
          eso muestra liderazgo y gestión. Si trabajás con represas, eso muestra dominio técnico profundo.
        </p>
        <p>
          <strong className="text-white">Elegí un proyecto que te apasione.</strong> Cuando hablás de algo que
          te gusta, el video sale natural, el README sale con ganas, y la pasión se nota. Un recruiter
          distingue entre alguien que hizo un proyecto por obligación y alguien que lo disfrutó.
        </p>
        <p>
          <strong className="text-white">Challenges de entrevistas = proyectos.</strong> Si te dieron un
          desafío técnico en una entrevista y no quedaste, usá ese desafío como proyecto. "No me contrataste,
          perfecto — uso tu challenge como portfolio."
        </p>
        <p>
          <strong className="text-white">La mejor forma de vender es sin vender.</strong> Compartí tu valor,
          compartí tu pasión. Cuando mostrás lo que sabés hacer con genuino entusiasmo, las oportunidades llegan solas.
        </p>
      </>
    ),
  },
];

export default function PresentacionProyecto() {
  const totalItems = sections.reduce((sum, s) => sum + s.items.length, 0);

  return (
    <div className="min-h-screen bg-black font-sans">
      {/* Nav */}
      <nav className="fixed top-0 w-full bg-black/80 backdrop-blur-md border-b border-green-500/30 z-50">
        <div className="max-w-6xl mx-auto px-6 py-3 flex justify-between items-center">
          <Link to="/" className="text-xl font-bold text-green-400">Luciano Argolo</Link>
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-green-400/70 hover:text-green-300 transition-colors">Home</Link>
            <Link to="/cursos" className="text-green-400 hover:text-green-300 transition-colors font-semibold">Cursos</Link>
            <Link to="/cursos/presentacion-proyectos" className="text-green-400/70 hover:text-green-300 transition-colors">
              ← Volver al curso
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-24 pb-12 px-6">
        <MatrixBackground color="#00ff41" />
        <div className="absolute inset-0 bg-black/80 z-[1]" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 rounded-full bg-green-500/10 border border-green-500/20 px-4 py-1.5 text-sm text-green-400 mb-6">
              <span>📋</span>
              <span>{totalItems} pasos en total</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Cómo Presentar un Proyecto
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Lineamientos para que un proyecto de Data Engineering se convierta en una carta de presentación
              profesional. Repo, video, LinkedIn y diferencial personal.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Sections */}
      <section className="relative pb-24 px-6">
        <MatrixBackground color="#00ff41" />
        <div className="absolute inset-0 bg-black/80 z-[1]" />
        <div className="relative z-10 max-w-4xl mx-auto space-y-4">
          {sections.map((section, i) => (
            <SectionCard key={section.id} section={section} index={i} />
          ))}

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-8 rounded-xl border border-green-500/20 bg-green-500/5 p-6 md:p-8 text-center"
          >
            <h3 className="text-xl font-bold text-white mb-2">Siguiente paso</h3>
            <p className="text-gray-400 text-sm max-w-xl mx-auto mb-4">
              No busques la perfección. Hacé algo bueno que te dé una respuesta y después vas iterando.
              <strong className="text-white"> Preferible hecho antes que perfecto.</strong>
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
